import Axios, {
    AxiosAdapter,
    AxiosRequestConfig,
    CanceledError,
    GenericAbortSignal,
    InternalAxiosRequestConfig,
} from 'axios';
import pLimit from 'p-limit';

import { API_URL } from '@/config';

/**
 * The transport options a fetcher function accepts on top of its own parameters.
 *
 * Passed through `requestConfig` into the axios request config, so a fetcher taking this can be handed
 * the `signal` react-query gives to `queryFn` and its request is aborted once nothing observes the query.
 */
export type RequestOptions = {
    signal?: AbortSignal;
    /**
     * How a public share link authenticates, in place of the session cookie. The API reads it from the
     * `view_token` header, which `requestConfig` is what puts it in.
     */
    viewToken?: string | null;
};

/**
 * The axios request config for `options`.
 *
 * Every fetcher takes `RequestOptions` as its last argument and spreads this into its own config, so the
 * one translation from `viewToken` to the header carrying it lives here.
 */
export const requestConfig = ({ viewToken, ...options }: RequestOptions = {}): AxiosRequestConfig => ({
    ...options,
    ...(viewToken && { headers: { view_token: viewToken } }),
});

/**
 * How many requests are in flight at once across every client below.
 *
 * Nothing above this throttles them. The app is served over HTTP/2, which multiplexes up to a hundred
 * streams onto a single connection, so a page asking for forty windows puts all forty on the wire at once.
 * This cap is therefore the only bound on what one page can aim at the API, and the queue it creates is
 * where a request abandoned before its turn is dropped without being sent at all.
 */
const CONCURRENCY_LIMIT = 8;

const limit = pLimit(CONCURRENCY_LIMIT);

const defaultAdapter = Axios.getAdapter(Axios.defaults.adapter!);

/** Rejects as a cancellation the moment `signal` aborts, and otherwise never settles. */
const rejectWhenAborted = (signal: GenericAbortSignal) =>
    new Promise<never>((_resolve, reject) => {
        signal.addEventListener?.('abort', () => reject(new CanceledError('canceled')), { once: true });
    });

/**
 * Runs each request through the shared queue.
 *
 * Axios tests the signal once on the way in and never again, so a request abandoned while it waits for a
 * slot is still a live request as far as axios is concerned. Two things answer for it here. The test
 * inside the queue runs when the slot frees, and keeps an abandoned request from being sent. The race
 * below tells the caller straight away, rather than at the end of a wait it no longer needs.
 */
const limitedAdapter: AxiosAdapter = (config) => {
    const dispatched = limit(() => {
        if (config.signal?.aborted) {
            throw new CanceledError('canceled');
        }
        return defaultAdapter(config);
    });

    const { signal } = config;

    if (!signal?.addEventListener) {
        return dispatched;
    }

    return Promise.race([dispatched, rejectWhenAborted(signal)]);
};

function authRequestInterceptor(config: InternalAxiosRequestConfig) {
    config.headers.Accept = 'application/json';
    config.withCredentials = true;
    return config;
}

/**
 * A cancellation is passed through as it is, so `Axios.isCancel` still recognises it: the copy the
 * message normalisation below makes is a plain object, and callers have to tell an abandoned request
 * apart from a failed one to know whether to fall back.
 */
function rejectWithMessage(error: { response?: { data?: { message?: string } }; message?: string }) {
    if (Axios.isCancel(error)) {
        return Promise.reject(error);
    }

    const message = error.response?.data?.message || error.message;
    return Promise.reject({ ...error, message });
}

export const axios = Axios.create({
    baseURL: API_URL,
    adapter: limitedAdapter,
});

axios.interceptors.request.use(authRequestInterceptor);
axios.interceptors.response.use((response) => {
    return response.data;
}, rejectWithMessage);

export const unAuthAxios = Axios.create({
    baseURL: API_URL,
    adapter: limitedAdapter,
});

unAuthAxios.interceptors.response.use((response) => {
    return response.data;
}, rejectWithMessage);
